'use client';

import { lazy, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import dynamic from 'next/dynamic';
import { useTenantSlug } from '@/hooks/use-tenant-slug';
import { SearchPageSkeleton as DgrSearchPageSkeleton } from '@/tenants/dgr/search/SearchPageSkeleton';
import { SearchPageSkeleton as DgdSearchPageSkeleton } from '@/tenants/dgd/search/SearchPageSkeleton';
import { SearchPageSkeleton as UwrSearchPageSkeleton } from '@/tenants/uwr/search/SearchPageSkeleton';

// Client-side dynamic imports with ssr: false
// These are wrapped in a client component to avoid the Server Component restriction.
// Per-tenant variants pick the right tenant folder so DGD users get DGD-branded
// client pages and DGR users get DGR-branded ones.
//
// NOTE: /my/* pages have been migrated to App Router at app/(tenant)/(entrant)/my/*
// Their client imports have been removed from this file.

// ---------- Search ----------

const LazyDgrSearchPage = lazy(() =>
  import('@/tenants/dgr/search/SearchPage').then((m) => ({ default: m.SearchPage })),
);
const LazyDgdSearchPage = lazy(() =>
  import('@/tenants/dgd/search/SearchPage').then((m) => ({ default: m.SearchPage })),
);
const LazyUwrSearchPage = lazy(() =>
  import('@/tenants/uwr/search/SearchPage').then((m) => ({ default: m.SearchPage })),
);

export function SearchPageClient() {
  const tenantSlug = useTenantSlug() ?? 'dgr';
  const searchParams = useSearchParams();
  const query = searchParams.get('q') ?? undefined;
  const Skeleton =
    tenantSlug === 'dgd'
      ? DgdSearchPageSkeleton
      : tenantSlug === 'uwr'
        ? UwrSearchPageSkeleton
        : DgrSearchPageSkeleton;
  const SearchPage =
    tenantSlug === 'dgd'
      ? LazyDgdSearchPage
      : tenantSlug === 'uwr'
        ? LazyUwrSearchPage
        : LazyDgrSearchPage;

  return (
    <Suspense fallback={<Skeleton query={query} />}>
      <SearchPage />
    </Suspense>
  );
}

// ---------- Global Map ----------

const DgrGlobalMapPage = dynamic(
  () => import('@/tenants/dgr/events/GlobalMapPage').then((m) => m.GlobalMapPage),
  { ssr: false },
);
const DgdGlobalMapPage = dynamic(
  () => import('@/tenants/dgd/events/GlobalMapPage').then((m) => m.GlobalMapPage),
  { ssr: false },
);

export function GlobalMapPageClient() {
  const tenantSlug = useTenantSlug() ?? 'dgr';
  return tenantSlug === 'dgd' ? <DgdGlobalMapPage /> : <DgrGlobalMapPage />;
}

// ---------- Sponsor ----------

const DgrSponsorPage = dynamic(
  () => import('@/tenants/dgr/sponsor/SponsorPage').then((m) => m.SponsorPage),
  { ssr: false },
);
const DgdSponsorPage = dynamic(
  () => import('@/tenants/dgd/sponsor/SponsorPage').then((m) => m.SponsorPage),
  { ssr: false },
);

export function SponsorPageClient() {
  const tenantSlug = useTenantSlug() ?? 'dgr';
  return tenantSlug === 'dgd' ? <DgdSponsorPage /> : <DgrSponsorPage />;
}

const DgrSponsorThankYouPage = dynamic(
  () => import('@/tenants/dgr/sponsor/SponsorThankYouPage').then((m) => m.SponsorThankYouPage),
  { ssr: false },
);
const DgdSponsorThankYouPage = dynamic(
  () => import('@/tenants/dgd/sponsor/SponsorThankYouPage').then((m) => m.SponsorThankYouPage),
  { ssr: false },
);

export function SponsorThankYouPageClient() {
  const tenantSlug = useTenantSlug() ?? 'dgr';
  return tenantSlug === 'dgd' ? <DgdSponsorThankYouPage /> : <DgrSponsorThankYouPage />;
}

// ---------- Stripe Checkout ----------

const SharedStripeCheckoutPage = dynamic(
  () => import('@/tenants/_shared/pages/StripeCheckoutPage').then((m) => m.StripeCheckoutPage),
  { ssr: false },
);

export function StripeCheckoutPageClient() {
  const tenantSlug = useTenantSlug() ?? 'dgr';
  // DGR is the only dark-chrome tenant; light tenants (dgd, uwr, …) get the light checkout.
  return <SharedStripeCheckoutPage theme={tenantSlug === 'dgr' ? 'dark' : 'light'} />;
}

// ---------- Fundraiser ----------

const DgrFundraiserProfilePage = dynamic(
  () =>
    import('@/tenants/dgr/fundraiser/FundraiserProfilePage').then((m) => m.FundraiserProfilePage),
  { ssr: false },
);
const DgdFundraiserProfilePage = dynamic(
  () =>
    import('@/tenants/dgd/fundraiser/FundraiserProfilePage').then((m) => m.FundraiserProfilePage),
  { ssr: false },
);
const UwrFundraiserProfilePage = dynamic(
  () =>
    import('@/tenants/uwr/fundraiser/FundraiserProfilePage').then((m) => m.FundraiserProfilePage),
  { ssr: false },
);

export function FundraiserProfilePageClient(
  props: React.ComponentProps<typeof DgrFundraiserProfilePage>,
) {
  const tenantSlug = useTenantSlug() ?? 'dgr';
  if (tenantSlug === 'dgd') return <DgdFundraiserProfilePage {...props} />;
  if (tenantSlug === 'uwr') return <UwrFundraiserProfilePage {...props} />;
  return <DgrFundraiserProfilePage {...props} />;
}

// ---------- Passenger Invite ----------

const DgrPassengerInvitePage = dynamic(
  () =>
    import('@/tenants/dgr/passenger-invite/PassengerInvitePage').then((m) => m.PassengerInvitePage),
  { ssr: false },
);
const DgdPassengerInvitePage = dynamic(
  () =>
    import('@/tenants/dgd/passenger-invite/PassengerInvitePage').then((m) => m.PassengerInvitePage),
  { ssr: false },
);
const UwrPassengerInvitePage = dynamic(
  () =>
    import('@/tenants/uwr/passenger-invite/PassengerInvitePage').then((m) => m.PassengerInvitePage),
  { ssr: false },
);

export function PassengerInvitePageClient(
  props: React.ComponentProps<typeof DgrPassengerInvitePage>,
) {
  const tenantSlug = useTenantSlug() ?? 'dgr';
  if (tenantSlug === 'dgd') return <DgdPassengerInvitePage {...props} />;
  if (tenantSlug === 'uwr') return <UwrPassengerInvitePage {...props} />;
  return <DgrPassengerInvitePage {...props} />;
}
